import { useMemo, useState } from 'react';
import styled from 'styled-components';
import type { Activity, ActivityTiming, Dependency } from '@entities/ksp/types';
import { decimal, projectDate } from '@entities/ksp/format';
import { Field, Input, Row, Select, Small } from './parts';

const Viewport = styled.div`
  height: 480px;
  overflow: auto;
  position: relative;
  border: 1px solid ${({ theme }) => theme.colors.border};
`;
const Canvas = styled.div<{ $height: number; $width: number }>`
  height: ${({ $height }) => $height}px;
  position: relative;
  min-width: ${({ $width }) => $width}px;
`;
const Line = styled.div<{ $top: number }>`
  position: absolute;
  top: ${({ $top }) => $top}px;
  left: 0;
  right: 0;
  height: 48px;
  display: grid;
  grid-template-columns: 280px 110px 110px 110px 90px 1fr;
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 12px;
  gap: 8px;
`;
const Cell = styled.div`
  padding-left: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const Plot = styled.div`
  position: relative;
  height: 42px;
  background: repeating-linear-gradient(
    to right,
    transparent 0,
    transparent 13.9%,
    ${({ theme }) => theme.colors.border} 14%,
    transparent 14.1%
  );
`;
const Bar = styled.button<{
  $left: number;
  $width: number;
  $critical: boolean;
  $baseline?: boolean;
}>`
  position: absolute;
  top: ${({ $baseline }) => ($baseline ? 27 : 5)}px;
  height: ${({ $baseline }) => ($baseline ? 5 : 16)}px;
  left: ${({ $left }) => $left}%;
  width: ${({ $width }) => $width}%;
  min-width: 3px;
  border: 0;
  border-radius: 3px;
  padding: 0;
  background: ${({ theme, $critical, $baseline }) => ($baseline ? theme.colors.text.muted : $critical ? theme.colors.status.danger : theme.colors.primary)};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'grab')};
`;

export function Gantt({
  activities,
  timings,
  dependencies,
  timezone,
  readonly,
  onEdit,
  onMove,
}: {
  activities: Activity[];
  timings: ActivityTiming[];
  dependencies: Dependency[];
  timezone: string;
  readonly: boolean;
  onEdit: (activity: Activity) => void;
  onMove: (activity: Activity, proposed: string, affected: string[]) => void;
}) {
  const [scroll, setScroll] = useState(0);
  const [search, setSearch] = useState('');
  const [critical, setCritical] = useState(false);
  const [scale, setScale] = useState('week');
  const [drag, setDrag] = useState<{ id: string; x: number } | null>(null);
  const byId = useMemo(() => new Map(timings.map((t) => [t.id, t])), [timings]);
  const successors = useMemo(() => {
    const result = new Map<string, string[]>();
    for (const edge of dependencies) {
      const list = result.get(edge.predecessor_id) ?? [];
      list.push(edge.successor_id);
      result.set(edge.predecessor_id, list);
    }
    return result;
  }, [dependencies]);
  const filtered = useMemo(
    () =>
      activities.filter(
        (a) =>
          `${a.code} ${a.name} ${a.wbs_id ?? ''}`.toLowerCase().includes(search.toLowerCase()) &&
          (!critical || byId.get(a.id)?.critical),
      ),
    [activities, search, critical, byId],
  );
  const instants = [
    ...timings.flatMap((t) => [Date.parse(t.early_start), Date.parse(t.early_finish)]),
    ...activities.flatMap((activity) => [
      Date.parse(activity.baseline_start ?? ''),
      Date.parse(activity.baseline_finish ?? ''),
    ]),
  ].filter(Number.isFinite);
  const from = instants.length ? Math.min(...instants) : Date.now();
  const to = instants.length ? Math.max(...instants) : from + 86400000;
  const span = Math.max(to - from, 86400000);
  const start = Math.max(0, Math.floor(scroll / 48) - 5);
  const visible = filtered.slice(start, start + 22);
  const position = (a: string, b: string) => ({
    left: Math.max(0, ((Date.parse(a) - from) / span) * 100),
    width: Math.max(0, ((Date.parse(b) - Date.parse(a)) / span) * 100),
  });
  const moved = (activity: Activity, x: number, width: number) => {
    if (!drag || drag.id !== activity.id || readonly) return;
    const timing = byId.get(activity.id);
    const unit = scale === 'day' ? 86400000 : scale === 'week' ? 7 * 86400000 : 30 * 86400000;
    const delta = Math.round((((x - drag.x) / width) * span) / unit) * unit;
    setDrag(null);
    if (!timing || delta === 0) return;
    const affected = new Set<string>();
    const pending = [activity.id];
    while (pending.length) {
      const id = pending.shift()!;
      for (const successor of successors.get(id) ?? []) {
        if (!affected.has(successor)) {
          affected.add(successor);
          pending.push(successor);
        }
      }
    }
    onMove(activity, new Date(Date.parse(timing.early_start) + delta).toISOString(), [...affected]);
  };
  return (
    <>
      <Row>
        <Field>
          Поиск по коду, работе, WBS
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setScroll(0);
            }}
          />
        </Field>
        <Field>
          Шаг переноса
          <Select value={scale} onChange={(e) => setScale(e.target.value)}>
            <option value="day">Сутки</option>
            <option value="week">Неделя</option>
            <option value="month">Месяц</option>
          </Select>
        </Field>
        <label>
          <input
            type="checkbox"
            checked={critical}
            onChange={(e) => setCritical(e.target.checked)}
          />{' '}
          Критические
        </label>
      </Row>
      <Small>
        Работ: {filtered.length}. {projectDate(new Date(from).toISOString(), timezone)} —{' '}
        {projectDate(new Date(to).toISOString(), timezone)} · {timezone}. Цветная полоса — прогноз,
        серая — baseline. Резерв: до расчётного конца CPM.
      </Small>
      <Viewport onScroll={(e) => setScroll(e.currentTarget.scrollTop)}>
        <Canvas
          $height={Math.max(48, filtered.length * 48)}
          $width={Math.max(
            1180,
            748 + (span / 86400000) * (scale === 'day' ? 40 : scale === 'week' ? 10 : 3),
          )}
        >
          {visible.map((activity, index) => {
            const timing = byId.get(activity.id);
            const pos = timing ? position(timing.early_start, timing.early_finish) : null;
            const baseline =
              activity.baseline_start && activity.baseline_finish
                ? position(activity.baseline_start, activity.baseline_finish)
                : null;
            return (
              <Line $top={(start + index) * 48} key={activity.id}>
                <Cell>
                  <button onClick={() => onEdit(activity)}>{activity.code}</button> {activity.name}
                </Cell>
                <Cell>
                  {decimal(activity.quantity)} {activity.unit}
                </Cell>
                <Cell>
                  {timing ? projectDate(timing.early_start, timezone) : 'Прогноз неизвестен'}
                </Cell>
                <Cell>{timing ? projectDate(timing.early_finish, timezone) : '—'}</Cell>
                <Cell title="Общий резерв, минуты">{timing?.total_float_minutes ?? '—'} мин</Cell>
                <Plot>
                  {pos && (
                    <Bar
                      aria-label={`${activity.code} прогноз`}
                      title={activity.name}
                      $left={pos.left}
                      $width={pos.width}
                      $critical={!!timing?.critical}
                      disabled={readonly}
                      onPointerDown={(e) => {
                        e.currentTarget.setPointerCapture(e.pointerId);
                        setDrag({ id: activity.id, x: e.clientX });
                      }}
                      onPointerUp={(e) =>
                        moved(activity, e.clientX, e.currentTarget.parentElement?.clientWidth ?? 1)
                      }
                    />
                  )}
                  {baseline && (
                    <Bar
                      tabIndex={-1}
                      aria-label="baseline"
                      disabled
                      $baseline
                      $left={baseline.left}
                      $width={baseline.width}
                      $critical={false}
                    />
                  )}
                </Plot>
              </Line>
            );
          })}
        </Canvas>
      </Viewport>
    </>
  );
}
